import { useEffect, useRef, useState } from 'react';

import Collapse from '@material-ui/core/Collapse';

import { type Location, locationUserPermission } from '@cobliteam/common-types';

import { Analytics } from '@cobliteam/analytics';

import {
  MenuItem,
  MenuItemType,
  PermissionsGate,
  Popper,
} from '@cobliteam/base-components';

import cn from '@cobliteam/ui/utils/cn';

import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';

export interface SidebarItemDropdownProps {
  title: string;
  active: boolean;
  sidebarOpen: boolean;
  icon: JSX.Element;
  children: React.ReactNode;
  badge?: React.ReactNode;
  dataTest?: string;
  dataCy?: string;
  groupedLocations: Location[];
}

export const SidebarItemDropdown = ({
  title,
  active,
  sidebarOpen,
  icon,
  children,
  badge,
  dataTest,
  dataCy,
  groupedLocations,
}: SidebarItemDropdownProps) => {
  const [open, setOpen] = useState(active);
  const [hover, setHover] = useState(false);
  const [popperHover, setPopperHover] = useState(false);
  const [transitionGuard, setTransitionGuard] = useState(false);
  const [anchorEl, setAnchorEl] = useState<any | null>(null);
  const [isVisible, setIsVisible] = useState(true);

  const parentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setOpen(active);
  }, [active]);

  const hasActivity = () => {
    return transitionGuard || popperHover;
  };

  const isActive = () => {
    return hover || popperHover || active || (open && sidebarOpen);
  };

  const handleMouseEnter = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setHover(true);
    setTimeout(() => setTransitionGuard(true), 150);
  };

  const handleMouseLeave = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setHover(false);
    setTimeout(() => setTransitionGuard(false), 150);
  };

  const handlePopperMouseLeave = () => {
    setPopperHover(false);
    setTransitionGuard(true);
    setTimeout(() => setTransitionGuard(false), 150);
  };

  const onClick = () => {
    Analytics.track('Sidebar Group Dropdown Clicked', { open });
    setOpen(!open);
  };

  const dropDownPermissions = (locations: Location[]) => {
    return locations.map((location) => locationUserPermission[location]);
  };

  useEffect(() => {
    // Check if all children elements have been rendered
    if (parentRef.current && !parentRef.current.children.length) {
      setIsVisible(false);
    }
  }, [parentRef]);

  if (!isVisible) return null;

  return (
    <PermissionsGate anyOfPermissions={dropDownPermissions(groupedLocations)}>
      <a
        // className={styles.sidebarItem(isActive())}
        className={cn(
          'flex h-12 w-full cursor-pointer items-center gap-2.5 whitespace-nowrap pl-5 pr-2.5 hover:text-white',
          isActive() ? 'bg-secondary-dark' : 'bg-none',
          sidebarOpen ? 'justify-between' : 'justify-center p-0'
        )}
        onClick={onClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        data-test={dataTest}
        id={title.replace(' ', '')}
      >
        <div
          className={cn(
            'flex items-center gap-2.5',
            sidebarOpen ? '' : 'w-full justify-center'
          )}
        >
          <div className='[&>svg]:size-4'>{icon}</div>

          {sidebarOpen ? (
            <MenuItem type={MenuItemType.LIGHT} active={isActive()}>
              {title}
            </MenuItem>
          ) : null}
        </div>

        {sidebarOpen ? (
          <div className='flex items-center gap-2.5'>
            {badge}
            <KeyboardArrowDownRoundedIcon
              className={cn(
                `size - 4 no - - all duration - 500 ease -in -out transform underline transition`,
                open ? '-rotate-180' : 'rotate-0'
              )}
            />
          </div>
        ) : null}
      </a>

      {sidebarOpen && (
        <Collapse in={open} data-cy={dataCy} ref={parentRef}>
          <div className='flex'>
            <div className='relative ml-7 w-0.5 rounded bg-gray' />
            <div className='flex w-full flex-col'>{children}</div>
          </div>
        </Collapse>
      )}

      {!sidebarOpen && hasActivity() && (
        <Popper
          open
          arrow={false}
          placement='right'
          paperClassName='min-w-48 -ml-2 py-2 bg-secondary-dark text-white rounded-md shadow-md rounded border-none'
          handleMouseEnter={() => {
            setPopperHover(true);
            setTransitionGuard(true);
          }}
          handleMouseLeave={handlePopperMouseLeave}
          anchorEl={anchorEl}
        >
          <div className='flex'>
            <div className='relative w-0.5 rounded bg-gray' />
            <div className='flex flex-col'>{children}</div>
          </div>
        </Popper>
      )}
    </PermissionsGate>
  );
};
